import { Box, Text } from "@mantine/core";
import { RichTextEditor } from "@mantine/tiptap";
import { useQueryClient } from "@tanstack/react-query";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect, useMemo, type ReactNode } from "react";
import {
	messageToDocument,
	serializeMessageDocument,
	type MessageFormat,
} from "../messageDocument";
import { staffMentionExtension } from "../staffMention";
import classes from "./ConversationMessageContent.module.css";

export function ConversationMessageEditor({
	value,
	format = "rich-text",
	onChange,
	disabled = false,
	label,
    description,
	placeholder = "Write a message. Type @ to tag staff.",
	action,
}: {
	value: string;
	format?: MessageFormat;
	onChange: (message: string) => void;
	disabled?: boolean;
	label?: string;
	placeholder?: string;
	description?: string;
	action?: ReactNode;
}) {
	const client = useQueryClient();
	const extensions = useMemo(
		() => [
			StarterKit.configure({
				heading: false,
				horizontalRule: false,
				code: false,
				codeBlock: false,
				link: { openOnClick: false },
			}),
			Placeholder.configure({ placeholder }),
			staffMentionExtension(client),
		],
		[client, placeholder],
	);
	const editor = useEditor({
		immediatelyRender: false,
		extensions,
		content: messageToDocument(value, format),
		editable: !disabled,
		editorProps: {
			attributes: {
				"aria-label": label ?? "Message",
				"aria-multiline": "true",
			},
		},
		onUpdate: ({ editor: updated }) =>
			onChange(serializeMessageDocument(updated.getJSON())),
	});
	useEffect(() => {
		if (editor && !editor.isDestroyed) editor.setEditable(!disabled, false);
	}, [editor, disabled]);
	useEffect(() => {
		if (!editor || editor.isDestroyed) return;
		let cancelled = false;
		// Mention node views mount React portals. Sync outside React's effect
		// phase, and recheck the editor because it may have been torn down.
		queueMicrotask(() => {
			if (!cancelled && !editor.isDestroyed && serializeMessageDocument(editor.getJSON()) !== value)
				editor.commands.setContent(messageToDocument(value, format), { emitUpdate: false });
		});
		return () => { cancelled = true; };
	}, [editor, value, format]);
	return (
		<Box w="100%">
			{label && (
				<Text size="sm" fw={500} mb={description ? 0 : 4}>
					{label}
				</Text>
			)}
            {description && (
				<Text size="xs" c="dimmed" mb={4}>
					{description}
				</Text>
			)}
			<div inert={disabled}>
				<RichTextEditor
					editor={editor && !editor.isDestroyed ? editor : null}
					classNames={{ content: classes.editor }}
					styles={{ root: { width: "100%" } }}
                    variant="subtle"
				>
					<RichTextEditor.Toolbar>
						<RichTextEditor.ControlsGroup>
							<RichTextEditor.Bold />
							<RichTextEditor.Italic />
							<RichTextEditor.Underline />
							<RichTextEditor.Strikethrough />
						</RichTextEditor.ControlsGroup>
						<RichTextEditor.ControlsGroup>
							<RichTextEditor.BulletList />
							<RichTextEditor.OrderedList />
							<RichTextEditor.Blockquote />
						</RichTextEditor.ControlsGroup>
						<RichTextEditor.ControlsGroup>
							<RichTextEditor.Link />
							<RichTextEditor.Unlink />
						</RichTextEditor.ControlsGroup>
					</RichTextEditor.Toolbar>
					<Box
						pos="relative"
						className={action ? classes.withAction : undefined}
					>
						<RichTextEditor.Content />
						{action && (
							<Box pos="absolute" right={8} bottom={8}>
								{action}
							</Box>
						)}
					</Box>
				</RichTextEditor>
			</div>
		</Box>
	);
}
